import React, { useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useColorModeValue,
  Divider,
  Badge,
} from '@chakra-ui/react'
import {
  HamburgerIcon,
  CloseIcon,
  SettingsIcon,
  BellIcon,
} from '@chakra-ui/icons'
import { useAuth } from '../contexts/AuthContext'
import Sidebar from './Sidebar'
import AnnouncementPopup from './AnnouncementPopup'
import { hasPermission, type Role } from '../config/permissions'

const AdminLayout: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { user, logout, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Hook들을 조건부 렌더링 이전에 호출
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // 인증 상태 확인
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, isLoading, navigate])

  // 로딩 중이거나 인증되지 않은 경우 로딩 표시
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="100vh">
        <Text>로딩 중...</Text>
      </Box>
    )
  }

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="100vh">
        <Text>로그인 페이지로 이동 중...</Text>
      </Box>
    )
  }

  const handleLogout = () => {
    logout()
  }

  const getPageTitle = () => {
    const path = location.pathname
    switch (path) {
      case '/admin/dashboard':
        return '대시보드'
      case '/admin/faqs':
        return '자주하는 질문 관리'
      case '/admin/logs':
        return '쿼리 로그'
      case '/admin/crawler-logs':
        return '웹 크롤링 로그'
      case '/admin/workflow-logs':
        return '워크플로우 로그'
      case '/admin/files':
        return '파일 관리'
      case '/admin/prompt-rules':
        return '프롬프트 규칙'
      case '/admin/analysis-report':
        return '분석 리포트'
      case '/admin/validation-report':
        return '검증 리포트'
      case '/admin/announcements':
        return '공지사항'
      case '/admin/inquiry-board':
        return '문의게시판'
      case '/admin/settings':
        return '설정'
      case '/admin/user-guide':
        return '사용자 가이드'
      default:
        return 'BEXCO 관리자'
    }
  }

  return (
    <Flex h="100vh" bg="gray.50">
      {/* 데스크톱 사이드바 */}
      <Box
        display={{ base: 'none', md: 'block' }}
        w="280px"
        bg={bgColor}
        borderRight="1px"
        borderColor={borderColor}
        boxShadow="sm"
      >
        <Sidebar />
      </Box>

      {/* 메인 컨텐츠 영역 */}
      <Flex direction="column" flex="1" overflow="hidden">
        {/* 상단 헤더 */}
        <Box
          bg={bgColor}
          px={6}
          py={4}
          borderBottom="1px"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              {/* 모바일 메뉴 버튼 */}
              <IconButton
                display={{ base: 'flex', md: 'none' }}
                aria-label="메뉴 열기"
                icon={<HamburgerIcon />}
                variant="ghost"
                onClick={onOpen}
              />
              
              {/* 페이지 제목 */}
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold" color="bexco.700">
                  {getPageTitle()}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  BEXCO AI 챗봇 관리 시스템
                </Text>
              </VStack>
            </HStack>

            {/* 우측 액션 버튼들 */}
            <HStack spacing={3}>
              <IconButton
                aria-label="알림"
                icon={<BellIcon />}
                variant="ghost"
                size="sm"
              />
              <IconButton
                aria-label="설정"
                icon={<SettingsIcon />}
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/settings')}
              />
              
              <Divider orientation="vertical" h="20px" />
              
              <VStack align="end" spacing={0}>
                <Text fontSize="sm" fontWeight="medium">
                  {user?.username}
                </Text>
                <Badge colorScheme="bexco" size="sm">
                  관리자
                </Badge>
              </VStack>
              
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={handleLogout}
              >
                로그아웃
              </Button>
            </HStack>
          </Flex>
        </Box>

        {/* 메인 컨텐츠 */}
        <Box flex="1" overflow="auto" p={6}>
          <Outlet />
        </Box>
      </Flex>

      {/* 모바일 사이드바 드로어 */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text color="bexco.700" fontWeight="bold">
              BEXCO 관리자
            </Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <Sidebar onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* 공지사항 팝업 */}
      <AnnouncementPopup />
    </Flex>
  )
}

export default AdminLayout





